import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Row,
  Input,
  UncontrolledTooltip,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import AdminHeader from "components/Headers/AdminHeader";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { useDispatch, useSelector } from "react-redux";
import { getVisitorList } from "action/visitor";
import { visitorList } from "constants/visitor";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";

function VisitorsListing() {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const visitorDataList = useSelector((state) => state.visitors.visitorList);
  const [visitorListState, setVisitorListState] = useState({
    isLoading: true,
    isSearched: false,
    error: "",
    noRecords: false,
  });
  const [pageNum, setPageNum] = useState(1);

  const [searchState, setSearchState] = useState({
    searchName: "",
    searchOrganization: "",
    filterParams: [],
  });
  //visitor List added sr no. for each item
  const formattedVisitorList = visitorDataList?.data?.Visitor?.map(
    (item, index) => {
      return {
        ...item,
        srNo:
          (pageNum - 1) *
            (visitorDataList?.data?.noOfRecordsPerPage ||
              DEFAULT_RECORDS_PER_PAGE) +
          index +
          1,
      };
    },
  );

  const buildFilterParams = () => {
    const filters = [];

    if (searchState.searchName.trim()) {
      filters.push({
        field: "Name",
        condition: "like",
        filterValues: [searchState.searchName.trim()],
      });
    }

    if (searchState.searchOrganization.trim()) {
      filters.push({
        field: "Organization",
        condition: "like",
        filterValues: [searchState.searchOrganization.trim()],
      });
    }

    return filters.length ? JSON.stringify(filters) : [];
  };

  //api call for get technicians list
  useEffect(() => {
    getVisitorList(dispatch, pageNum, buildFilterParams());
    return () => {
      dispatch({ type: visitorList, payload: {} }); //to clear the redux state on unmount
    };
  }, [dispatch, pageNum, searchState.filterParams]);

  //to check the visitorList response and till the time show loader or error as per response
  useEffect(() => {
    if (visitorDataList?.code === 200) {
      setVisitorListState({
        isLoading: false,
        isSearched: false,
        error: "",
        noRecords: visitorDataList?.data?.Visitor?.length === 0,
      });
    } else if (visitorDataList?.code || visitorDataList?.message) {
      setVisitorListState({
        isLoading: false,
        isSearched: false,
        error:
          visitorDataList?.message ||
          "Failed to load visitor list! Please try again.",
        noRecords: false,
      });
    }
  }, [visitorDataList]);

  const columns = [
    { Header: "Sr.no.", accessor: "srNo" },
    {
      Header: "Name",
      accessor: "name",
    },
    {
      Header: "Email",
      accessor: "email",
      Cell: ({ value, row }) => {
        const tooltipId = `email-tooltip-${row.original.id}`;
        return (
          <>
            {/* Masked email */}
            <span id={tooltipId} className="cursor-pointer">
              {value || "-"}
            </span>
            <UncontrolledTooltip placement="top" target={tooltipId}>
              Email masked for privacy
            </UncontrolledTooltip>
          </>
        );
      },
    },
    {
      Header: "Designation",
      accessor: "designation",
      Cell: ({ value }) => value || "-",
    },
    {
      Header: "Organization",
      accessor: "organization",
      Cell: ({ value }) => value || "-",
    },
    {
      Header: "Visitor Verified On",
      accessor: "createdOn",
      Cell: ({ value }) => value || "-",
    },
    {
      Header: "Last Visited Date",
      accessor: "lastVisitedDate",
      Cell: ({ value }) => value || "-",
    },
    {
      Header: "Visit Count",
      accessor: "visitCount",
      Cell: ({ value }) => value ?? 0,
    },
    {
      Header: "Actions",
      disableSortBy: true,
      Cell: ({ row }) => {
        const encodedId = urlEncodedId(row.original.id); // encode ID here //value
        return (
          <div className="actions-right d-flex justify-content-start">
            <Button
              className="btn-link d-flex align-items-center p-0"
              id={`visit-details-${encodedId}`}
              size="sm"
              onClick={() => {
                navigate(
                  role === "SUPER_ADMIN"
                    ? `/super-admin/visitors/${encodedId}/details`
                    : `/admin/visitors/${encodedId}/details`,
                );
              }}
            >
              <i className="fas fa-eye mr-1" />
              {/* <span>View</span> */}
            </Button>

            <UncontrolledTooltip
              delay={0}
              target={`visit-details-${encodedId}`}
            >
              View
            </UncontrolledTooltip>
          </div>
        );
      },
    },
  ];

  const clearSearch = () => {
    setVisitorListState((prev) => ({
      ...prev,
      isSearched: false,
      isLoading: false,
    }));
    setSearchState({
      searchString: "",
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSearch = () => {
    if (
      !searchState.searchName.trim() &&
      !searchState.searchOrganization.trim()
    ) {
      return;
    }

    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(searchState.filterParams) !==
      JSON.stringify(newFilterParams)
    ) {
      setVisitorListState((prev) => ({
        ...prev,
        isLoading: true,
        isSearched: true,
      }));

      setSearchState((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));

      if (pageNum !== 1) setPageNum(1);
    }
  };

  const handleOnRowClick = (rowData) => {
    const encodedId = urlEncodedId(rowData.id); //rowData.ID
    navigate(
      role === "SUPER_ADMIN"
        ? `/super-admin/visitors/${encodedId}/details`
        : `/admin/visitors/${encodedId}/details`,
    );
  };

  return (
    <>
      <AdminHeader />
      <Container fluid className="mt--6">
        <Row>
          <Col md="12">
            <Card>
              {/* Header */}
              <CardHeader className="d-flex align-items-center justify-content-between">
                <CardTitle tag="h3" className="mb-0">
                  Visitors
                </CardTitle>
              </CardHeader>

              {/* Body */}
              <CardBody>
                <div className="d-flex align-items-center justify-content-between flex-wrap flex-column flex-md-row">
                  {/* Left side - Filter Inputs + Search */}
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by name"
                      className="mr-2 mb-2 filter-input-large"
                      value={searchState.searchName}
                      onChange={(e) =>
                        setSearchState((prev) => ({
                          ...prev,
                          searchName: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Input
                      type="text"
                      placeholder="Search by organization"
                      className="mr-2 mb-2 filter-input-large"
                      value={searchState.searchOrganization}
                      onChange={(e) =>
                        setSearchState((prev) => ({
                          ...prev,
                          searchOrganization: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      className="d-flex justify-content-center align-items-center mb-1 filter-btn btn-deep-blue"
                      onClick={handleSearch}
                      disabled={visitorListState.isSearched} //diabled the btn when loading
                    >
                      {visitorListState.isSearched ? (
                        <>
                          <i className="fa fa-spinner fa-spin loader fa-xl" />{" "}
                        </>
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" /> Search
                        </>
                      )}
                    </Button>

                    {searchState.filterParams?.searchString?.trim() && (
                      <Button
                        color="default"
                        className="d-flex align-items-start mb-1 ml-0 filter-btn btn-deep-blue px-4"
                        onClick={clearSearch}
                      >
                        <i className="fa fa-times mr-1 mt-1" /> Clear
                      </Button>
                    )}
                  </div>
                </div>
                <div className="mt-2 mb-3 mx-1">
                  {visitorDataList?.data?.totalRecords ? (
                    <>
                      {searchState.filterParams?.searchString?.trim() ? (
                        <span className="font-weight-bold">
                          Showing {visitorDataList?.data?.filteredRecords} of{" "}
                          {visitorDataList?.data?.totalRecords} Records
                        </span>
                      ) : (
                        <span className="total-records">
                          Total Records : {visitorDataList?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  ) : null}
                </div>

                {visitorListState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : visitorListState.error ? (
                  <>
                    <div className="loaderContainer">
                      {visitorListState.error}
                    </div>
                  </>
                ) : visitorListState.noRecords ? (
                  <div className="loaderContainer text-muted">
                    No visitors found.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      columns={columns}
                      data={formattedVisitorList || []}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                      activePage={pageNum}
                      setPageNum={setPageNum}
                      totalPages={Math.ceil(
                        visitorDataList?.data?.filteredRecords /
                          visitorDataList?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE,
                      )}
                      showPaginationBottom={true}
                      rowClick={handleOnRowClick}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default VisitorsListing;
